import _extends from "@babel/runtime/helpers/esm/extends";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["unstable_remote", "module", "fallback", "props"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
/* eslint-disable camelcase */

/* eslint-disable no-underscore-dangle */
import React, { Suspense, useCallback, useMemo } from 'react';
import { ErrorBoundary } from '@lumahealthhq/ui-core';
import { useLogger } from '@lumahealthhq/ui-utils';
import { mappingModulesRemote } from '../constants';
import { useFallbackRender } from '../hooks';

/**
 * Webpack init sharing function declaration
 */

/**
 * Webpack share scopes declaration
 */

/**
 * Extends Window interface to include remote containers and URLs
 */

/**
 * Container interface for remote modules
 */

/** Factory function type that returns a Module */

/** Module type containing default React component */

/**
 * Props for FederatedModule component
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * FederatedModule is a component that dynamically loads and renders a module from a remote container.
 * Uses Webpack Module Federation for loading remote modules at runtime.
 *
 * @example
 * ```tsx
 * // Load and render a remote auth login component
 * <FederatedModule<IContainerAuthLoginProps>
 *   module={EModule.ContainerAuthLogin}
 *   fallback={<FallbackContainerAuthLogin />}
 *   props={{
 *     onSuccess: () => console.log('Success!')
 *   }}
 * />
 * ```
 *
 * @see {@link https://webpack.js.org/concepts/module-federation/#dynamic-remote-containers}
 *
 * @template P - Type of props passed to loaded module
 * @param {FederatedModuleProps<P>} props - Federated module props
 * @returns {JSX.Element} Rendered module
 */
export default function FederatedModule(_ref) {
  var unstable_remote = _ref.unstable_remote,
    module = _ref.module,
    fallback = _ref.fallback,
    inProps = _ref.props,
    injectedProps = _objectWithoutProperties(_ref, _excluded);
  var logger = useLogger();
  var fallbackRender = useFallbackRender(module);

  /**
   * Loads a remote entry script if not already loaded
   *
   * @param {ERemote} remoteName - Name of remote to load
   * @returns {Promise<void>} Promise that resolves when remote is loaded
   */
  var loadRemoteEntry = useCallback(function (remoteName) {
    return new Promise(function (resolve, reject) {
      if (window[remoteName]) {
        resolve();
        return;
      }
      var remoteUrl = window.UINEXT_REMOTES[remoteName];
      if (!remoteUrl) {
        reject(new Error("Remote URL for \"".concat(remoteName, "\" not found.")));
        return;
      }
      var script = document.createElement('script');
      script.src = remoteUrl;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = function () {
        logger.debug("\"".concat(remoteName, "\" remote entry loaded"));
        resolve();
      };
      script.onerror = function () {
        reject(new Error("Failed to load remoteEntry for remote: ".concat(remoteName)));
      };
      document.head.appendChild(script);
    });
  }, [logger]);

  /**
   * Loads and initializes a remote component
   *
   * @returns {Promise<{default: React.ComponentType}>} Promise that resolves with component
   */
  var loadComponent = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var remote, container, factory, _Module;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          remote = unstable_remote || mappingModulesRemote[module];
          _context.prev = 1;
          _context.next = 4;
          return loadRemoteEntry(remote);
        case 4:
          _context.next = 6;
          return __webpack_init_sharing__('default');
        case 6:
          container = window[remote]; // or get the container somewhere else
          // Initialize the container, it may provide shared modules
          if (container.__initialized) {
            _context.next = 12;
            break;
          }
          _context.next = 10;
          return container.init(__webpack_share_scopes__["default"]);
        case 10:
          container.__initialized = true;
          logger.debug("\"".concat(remote, "\" container initialized"));
        case 12:
          _context.next = 14;
          return window[remote].get("./".concat(module));
        case 14:
          factory = _context.sent;
          _Module = factory();
          return _context.abrupt("return", {
            "default": _Module["default"]
          });
        case 19:
          _context.prev = 19;
          _context.t0 = _context["catch"](1);
          logger.error('Error loading federated module', {
            error: _context.t0,
            remote: remote,
            module: module
          });
          throw _context.t0;
        case 23:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 19]]);
  })), [logger, loadRemoteEntry, module, unstable_remote]);
  var Component = useMemo(function () {
    return /*#__PURE__*/React.lazy(loadComponent);
  }, [loadComponent]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallbackRender: fallbackRender,
    children: /*#__PURE__*/_jsx(Suspense, {
      fallback: fallback,
      children: /*#__PURE__*/_jsx(Component, _extends({}, _extends({}, inProps, injectedProps)))
    })
  });
}